import axios, { AxiosRequestConfig } from 'axios';
import { SYNTIRO_HONEY_API_ENDPOINT } from '../../env';
import { getAuthHeaderForUser, handleAuthError } from '../../lib/auth/auth.util';
import {
  AssignRecordBody,
  Batch,
  BulkApproveResult,
  BulkDeleteResult,
  CancelBatchResult,
  EntityLookupItem,
  GeographyCountry,
  EsgRecord,
  FileContext,
  FileContextOverride,
  FileDuplicateResolution,
  FileDuplicateSummary,
  FileListItem,
  FlagRecordBody,
  Job,
  MetricLookupItem,
  PeriodLookupItem,
  ProblemDetail,
  ProcessBatchOptions,
  ProcessBatchResult,
  ResolveDuplicatesResult,
  RecordEventItem,
  RecordGroupsQuery,
  RecordGroupsResult,
  RecordListQuery,
  RecordListResult,
  RecordSeriesQuery,
  RecordSeriesResult,
  RequestSpecialistBody,
  UpdateRecordPatch,
  EsgRecordUpdateResponse,
  AdminFilesResponse,
  FileEventItem,
  MetricAliasListQuery,
  MetricAliasListResult
} from '../../types/syntiro_honey';

/**
 * REST client for the Syntiro Honey ESG Data Ingestion backend.
 *
 * Contract source of truth: the committed `app/api/swagger.json` in the
 * `syntiro-honey` repo. Every call attaches the current user's Firebase
 * bearer token; every failure is converted into an RFC 7807 `ProblemDetail`
 * (thrown as `SyntiroHoneyApiError`) so the UI never has to unpick raw
 * axios errors.
 */

const FALLBACK_ERROR_DETAIL =
  'The ESG Data Ingestion service could not be reached or returned an unexpected error. Please try again.';

/**
 * Error thrown by every service function in this module. Carries the RFC 7807
 * problem payload so UI code can render `title`/`detail`/`status` directly.
 */
export class SyntiroHoneyApiError extends Error {
  /** The RFC 7807 problem payload extracted from (or synthesised for) the failed request. */
  public readonly problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail ?? problem.title ?? FALLBACK_ERROR_DETAIL);
    this.name = 'SyntiroHoneyApiError';
    this.problem = problem;
  }
}

/**
 * Narrow an unknown response body to an RFC 7807 problem payload.
 * @param {unknown} body Response body to inspect.
 * @returns {boolean}
 */
const isProblemDetail = (body: unknown): body is ProblemDetail => {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return false;
  }
  const candidate = body as Record<string, unknown>;
  return typeof candidate.title === 'string' || typeof candidate.detail === 'string';
};

/**
 * Convert any error raised while calling the Honey backend into a typed
 * RFC 7807 `ProblemDetail` with a safe fallback message. Exported for UI use
 * (e.g. mapping caught errors to a toast/banner).
 * @param {unknown} error The caught error (axios error, Error, or anything else).
 * @returns {ProblemDetail}
 */
export const toProblemDetail = (error: unknown): ProblemDetail => {
  if (error instanceof SyntiroHoneyApiError) {
    return error.problem;
  }
  if (axios.isAxiosError(error)) {
    const body: unknown = error.response?.data;
    if (isProblemDetail(body)) {
      return body;
    }
    return {
      type: 'about:blank',
      title: error.response ? 'Request failed' : 'Service unreachable',
      status: error.response?.status,
      detail: error.message || FALLBACK_ERROR_DETAIL
    };
  }
  return {
    type: 'about:blank',
    title: 'Unexpected error',
    detail: error instanceof Error && error.message ? error.message : FALLBACK_ERROR_DETAIL
  };
};

/**
 * Execute an authenticated request against the Honey backend and unwrap the
 * response body. Signs the user out on 401 (matching app-wide behaviour) and
 * rethrows every failure as a `SyntiroHoneyApiError`.
 * @param {AxiosRequestConfig} config Request config relative to the Honey base URL.
 * @returns {Promise<ResponseBody>}
 */
const honeyRequest = async <ResponseBody>(config: AxiosRequestConfig): Promise<ResponseBody> => {
  if (!SYNTIRO_HONEY_API_ENDPOINT) {
    // Without a base URL axios resolves RELATIVE — the request would hit the CRA
    // dev server and surface as a baffling HTML 404. Fail with the actual cause.
    throw new SyntiroHoneyApiError({
      type: 'about:blank',
      title: 'Honey API endpoint not configured',
      detail:
        'REACT_APP_SYNTIRO_HONEY_API_ENDPOINT is not set for this environment, so the ESG Data Ingestion backend cannot be reached.'
    });
  }
  try {
    const auth_header = await getAuthHeaderForUser();
    const response = await axios.request<ResponseBody>({
      baseURL: SYNTIRO_HONEY_API_ENDPOINT,
      ...config,
      headers: { ...auth_header, ...config.headers }
    });
    return response.data;
  } catch (err: unknown) {
    handleAuthError(err);
    throw new SyntiroHoneyApiError(toProblemDetail(err));
  }
};

/**
 * Upload a batch of source files with optional context hints. No processing
 * is queued yet — call `processBatch` to confirm.
 * @param {Array<File>} files Files to upload (multipart field `files`, repeated).
 * @param {FileContext | undefined} batch_context Default context applied to files without an override (sent as a JSON string).
 * @param {Array<FileContextOverride> | undefined} file_contexts Per-file context overrides keyed by filename (sent as a JSON string).
 * @returns {Promise<Batch>} The created batch with persisted file rows and per-file skip reasons.
 */
export const createBatch = async (
  files: Array<File>,
  batch_context?: FileContext,
  file_contexts?: Array<FileContextOverride>
): Promise<Batch> => {
  const form_data = new FormData();
  for (const file of files) {
    form_data.append('files', file);
  }
  if (batch_context !== undefined) {
    form_data.append('batchContext', JSON.stringify(batch_context));
  }
  if (file_contexts !== undefined) {
    form_data.append('fileContexts', JSON.stringify(file_contexts));
  }
  return honeyRequest<Batch>({ method: 'post', url: '/batches', data: form_data });
};

/**
 * Soft-delete an uploaded file (e.g. removed from the batch before confirming).
 * @param {string} id File id (UUID).
 * @returns {Promise<void>}
 */
export const deleteFile = async (id: string): Promise<void> => {
  await honeyRequest<void>({ method: 'delete', url: `/files/${encodeURIComponent(id)}` });
};

/**
 * Confirm an uploaded batch: queues each file and creates one ingestion job per file.
 * @param {string} id Batch id (UUID).
 * @param {ProcessBatchOptions | undefined} options Optional per-file acknowledgements:
 *   `process_as_new_file_ids` lists exact-duplicate files the uploader explicitly chose
 *   to process as new records (each is stamped PROCESS_AS_NEW server-side, so the
 *   review banner never re-asks).
 * @returns {Promise<ProcessBatchResult>} One job reference per queued file.
 */
export const processBatch = async (
  id: string,
  options?: ProcessBatchOptions
): Promise<ProcessBatchResult> => {
  const process_as_new = options?.process_as_new_file_ids ?? [];
  return honeyRequest<ProcessBatchResult>({
    method: 'post',
    url: `/batches/${encodeURIComponent(id)}/process`,
    data: process_as_new.length > 0 ? { processAsNewFileIds: process_as_new } : undefined
  });
};

/**
 * A file's duplicate-resolution state: the stored decision (null while unanswered)
 * plus how many of its active records collide with records already on Syntiro.
 * @param {string} id File id (UUID).
 * @returns {Promise<FileDuplicateSummary>} The banner's data.
 */
export const fetchFileDuplicateSummary = async (id: string): Promise<FileDuplicateSummary> => {
  return honeyRequest<FileDuplicateSummary>({
    method: 'get',
    url: `/files/${encodeURIComponent(id)}/duplicate-summary`
  });
};

/**
 * Apply the user's duplicate decision to a processed file. One decision per file:
 * repeating the same one is a no-op; a different one is refused (409).
 * @param {string} id File id (UUID).
 * @param {FileDuplicateResolution} resolution UPSERT | ONLY_NEW | PROCESS_AS_NEW.
 * @returns {Promise<ResolveDuplicatesResult>} What the decision did, for the outcome message.
 */
export const resolveFileDuplicates = async (
  id: string,
  resolution: FileDuplicateResolution
): Promise<ResolveDuplicatesResult> => {
  return honeyRequest<ResolveDuplicatesResult>({
    method: 'post',
    url: `/files/${encodeURIComponent(id)}/duplicate-resolution`,
    data: { resolution }
  });
};

/**
 * Cancel a batch the reviewer no longer wants to wait on: stops its not-yet-started
 * files and their queued jobs (a file already processing finishes). Idempotent.
 * @param {string} id Batch id (UUID).
 * @returns {Promise<CancelBatchResult>} How many files were cancelled.
 */
export const cancelBatch = async (id: string): Promise<CancelBatchResult> => {
  return honeyRequest<CancelBatchResult>({
    method: 'post',
    url: `/batches/${encodeURIComponent(id)}/cancel`
  });
};

/**
 * Fetch a single ingestion job by id (status polling).
 * @param {string} id Job id (UUID).
 * @returns {Promise<Job>}
 */
export const fetchJob = async (id: string): Promise<Job> => {
  return honeyRequest<Job>({ method: 'get', url: `/jobs/${encodeURIComponent(id)}` });
};

/**
 * Serialise a record-list query into wire query params, omitting undefined
 * fields. `needsFollowUp` is sent as the string "true"/"false" per the contract.
 * @param {RecordListQuery} query Query to serialise.
 * @returns {Record<string, string | number>}
 */
const buildRecordListParams = (query: RecordListQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (query.status !== undefined) params.status = query.status;
  if (query.excludeApproved !== undefined) params.excludeApproved = String(query.excludeApproved);
  if (query.band !== undefined) params.band = query.band;
  if (query.mappingStatus !== undefined) params.mappingStatus = query.mappingStatus;
  if (query.metricId !== undefined) params.metricId = query.metricId;
  if (query.metricGroup !== undefined) params.metricGroup = query.metricGroup;
  if (query.divisionId !== undefined) params.divisionId = query.divisionId;
  // The API matches an entity id only when told which org level it names, so the
  // pair is sent together or not at all.
  if (query.entityId !== undefined && query.entityLevel !== undefined) {
    params.entityId = query.entityId;
    params.entityLevel = query.entityLevel;
  }
  if (query.periodId !== undefined) params.periodId = query.periodId;
  if (query.fileId !== undefined) params.fileId = query.fileId;
  if (query.needsFollowUp !== undefined) params.needsFollowUp = String(query.needsFollowUp);
  if (query.mappedFromAlias !== undefined) params.mappedFromAlias = String(query.mappedFromAlias);
  if (query.flags !== undefined) params.flags = query.flags;
  if (query.assignedToId !== undefined) params.assignedToId = query.assignedToId;
  if (query.search !== undefined) params.search = query.search;
  if (query.rawLabel !== undefined) params.rawLabel = query.rawLabel;
  if (query.sort !== undefined) params.sort = query.sort;
  if (query.order !== undefined) params.order = query.order;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset !== undefined) params.offset = query.offset;
  return params;
};

/**
 * List ESG records for review (filter by status/band/metric/file, sort, paginate).
 * @param {RecordListQuery} query Filters, sort and pagination; undefined fields are omitted.
 * @returns {Promise<RecordListResult>}
 */
export const fetchRecords = async (query: RecordListQuery = {}): Promise<RecordListResult> => {
  return honeyRequest<RecordListResult>({
    method: 'get',
    url: '/records',
    params: buildRecordListParams(query)
  });
};

/**
 * Roll records up into groups (by category / entity / period) with per-group counts
 * and mapping-trust breakdown, for the collapsible review list. Takes the same
 * filters as the list (no pagination/sort) plus the grouping dimension.
 * @param {RecordGroupsQuery} query Filters + groupBy; undefined fields are omitted.
 * @returns {Promise<RecordGroupsResult>}
 */
export const fetchRecordGroups = async (
  query: RecordGroupsQuery = {}
): Promise<RecordGroupsResult> => {
  const params = buildRecordListParams(query);
  if (query.groupBy !== undefined) params.groupBy = query.groupBy;
  return honeyRequest<RecordGroupsResult>({ method: 'get', url: '/records/groups', params });
};

/** Page size used when collecting every matching record id (the list's max limit). */
const ALL_IDS_PAGE_SIZE = 200;

/**
 * Collect every record matching a query, paging past the list limit — the shared
 * engine behind {@link fetchAllRecordIds} and {@link fetchAllRecordIdsWithLabel}.
 * The first page yields the total; the remaining pages are fetched in parallel.
 * @param {RecordListQuery} query Filters identifying the records (sort/pagination ignored).
 * @returns {Promise<Array<EsgRecord>>} Every matching record, full shape.
 */
async function fetchAllRecords(query: RecordListQuery): Promise<Array<EsgRecord>> {
  const first = await fetchRecords({ ...query, limit: ALL_IDS_PAGE_SIZE, offset: 0 });
  const items = [...first.items];
  const pageCount = Math.ceil(first.total / ALL_IDS_PAGE_SIZE);
  if (pageCount <= 1) {
    return items;
  }
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      fetchRecords({ ...query, limit: ALL_IDS_PAGE_SIZE, offset: (index + 1) * ALL_IDS_PAGE_SIZE })
    )
  );
  rest.forEach((page) => items.push(...page.items));
  return items;
}

/**
 * Collect the ids of every record matching a query, paging past the list limit.
 * Used to approve a whole group in one action (the bulk endpoint takes ids).
 * @param {RecordListQuery} query Filters identifying the records (sort/pagination ignored).
 * @returns {Promise<Array<string>>} Every matching record id.
 */
export const fetchAllRecordIds = async (query: RecordListQuery = {}): Promise<Array<string>> =>
  (await fetchAllRecords(query)).map((item) => item.id);

/**
 * Collect the id and raw label of every record matching a query, paging past the
 * list limit. Unlike {@link fetchAllRecordIds}, this keeps `rawLabel` alongside
 * each id specifically so a caller filtering by label (e.g. the sibling-confirm
 * offer) can re-check it client-side rather than trusting the server's own
 * `rawLabel` query param unconditionally — defence-in-depth against that filter
 * ever silently regressing server-side.
 * @param {RecordListQuery} query Filters identifying the records (sort/pagination ignored).
 * @returns {Promise<Array<{ id: string; rawLabel: string }>>} Every matching record's id + label.
 */
export const fetchAllRecordIdsWithLabel = async (
  query: RecordListQuery = {}
): Promise<Array<{ id: string; rawLabel: string }>> =>
  (await fetchAllRecords(query)).map((item) => ({ id: item.id, rawLabel: item.rawLabel }));

/**
 * Serialise a record-series query into wire query params, omitting undefined
 * fields. `needsFollowUp` is sent as the string "true"/"false" per the contract.
 * @param {RecordSeriesQuery} query Query to serialise.
 * @returns {Record<string, string | number>}
 */
const buildRecordSeriesParams = (query: RecordSeriesQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  if (query.status !== undefined) params.status = query.status;
  if (query.band !== undefined) params.band = query.band;
  if (query.metricId !== undefined) params.metricId = query.metricId;
  if (query.fileId !== undefined) params.fileId = query.fileId;
  if (query.needsFollowUp !== undefined) params.needsFollowUp = String(query.needsFollowUp);
  if (query.assignedToId !== undefined) params.assignedToId = query.assignedToId;
  if (query.search !== undefined) params.search = query.search;
  return params;
};

/**
 * Fetch each mapped metric's story over time: records grouped by metric × entity,
 * each group's values ordered by reporting period. Unmapped records are excluded.
 * @param {RecordSeriesQuery} query Filters (no pagination); undefined fields are omitted.
 * @returns {Promise<RecordSeriesResult>}
 */
export const fetchRecordSeries = async (
  query: RecordSeriesQuery = {}
): Promise<RecordSeriesResult> => {
  return honeyRequest<RecordSeriesResult>({
    method: 'get',
    url: '/records/series',
    params: buildRecordSeriesParams(query)
  });
};

/**
 * Fetch a single ESG record by id. Records not owned by the caller return 404.
 * @param {string} id Record id (UUID).
 * @returns {Promise<EsgRecord>}
 */
export const fetchRecord = async (id: string): Promise<EsgRecord> => {
  return honeyRequest<EsgRecord>({ method: 'get', url: `/records/${encodeURIComponent(id)}` });
};

/**
 * Edit a record (value/metric/unit/site/qualitative verdict). Each supplied
 * field is audited as an EDITED event and gets confidence 1.0.
 * @param {string} id Record id (UUID).
 * @param {UpdateRecordPatch} patch Fields to change; omitted fields are untouched.
 * @returns {Promise<EsgRecordUpdateResponse>} The updated record, plus the alias-loop
 *   outcome when the edit confirmed a metric (drives the reviewer feedback toast).
 */
export const updateRecord = async (
  id: string,
  patch: UpdateRecordPatch
): Promise<EsgRecordUpdateResponse> => {
  return honeyRequest<EsgRecordUpdateResponse>({
    method: 'patch',
    url: `/records/${encodeURIComponent(id)}`,
    data: patch
  });
};

/**
 * Flag (or unflag) a record for follow-up. Orthogonal to its review status.
 * @param {string} id Record id (UUID).
 * @param {FlagRecordBody} body The follow-up marker to set.
 * @returns {Promise<EsgRecord>} The updated record (markers never confirm a
 *   metric, so there is no alias outcome on this route).
 */
export const flagRecord = async (id: string, body: FlagRecordBody): Promise<EsgRecord> => {
  return honeyRequest<EsgRecord>({
    method: 'post',
    url: `/records/${encodeURIComponent(id)}/flag`,
    data: body
  });
};

/**
 * Request (or clear) EP-specialist validation for a record.
 * @param {string} id Record id (UUID).
 * @param {RequestSpecialistBody} body The specialist-request marker to set.
 * @returns {Promise<EsgRecord>} The updated record (markers never confirm a
 *   metric, so there is no alias outcome on this route).
 */
export const requestSpecialist = async (
  id: string,
  body: RequestSpecialistBody
): Promise<EsgRecord> => {
  return honeyRequest<EsgRecord>({
    method: 'post',
    url: `/records/${encodeURIComponent(id)}/request-specialist`,
    data: body
  });
};

/**
 * Assign a record to a reviewer (null clears the assignment).
 * @param {string} id Record id (UUID).
 * @param {AssignRecordBody} body The reviewer uid to assign, or null.
 * @returns {Promise<EsgRecord>} The updated record (assignment never confirms a
 *   metric, so there is no alias outcome on this route).
 */
export const assignRecord = async (id: string, body: AssignRecordBody): Promise<EsgRecord> => {
  return honeyRequest<EsgRecord>({
    method: 'post',
    url: `/records/${encodeURIComponent(id)}/assign`,
    data: body
  });
};

/**
 * Approve a record so it goes live. Requires a mapped metric (400 otherwise).
 * Approving an AI-suggested mapping also confirms it (ADR-0002), so the response
 * carries the alias-loop outcome like `updateRecord` does.
 * @param {string} id Record id (UUID).
 * @returns {Promise<EsgRecordUpdateResponse>} The approved record + what it taught.
 */
export const approveRecord = async (id: string): Promise<EsgRecordUpdateResponse> => {
  return honeyRequest<EsgRecordUpdateResponse>({
    method: 'post',
    url: `/records/${encodeURIComponent(id)}/approve`
  });
};

/**
 * Reopen an approved record for correction, moving it back to pending review so it
 * can be edited, re-approved or deleted (404 if missing, 409 if not approved).
 * @param {string} id Record id (UUID).
 * @returns {Promise<EsgRecord>} The reopened record (now PENDING_REVIEW).
 */
export const reopenRecord = async (id: string): Promise<EsgRecord> => {
  return honeyRequest<EsgRecord>({
    method: 'post',
    url: `/records/${encodeURIComponent(id)}/reopen`
  });
};

/**
 * Soft-delete a record (404 if missing/deleted, 409 if already approved).
 * @param {string} id Record id (UUID).
 * @returns {Promise<void>}
 */
export const deleteRecord = async (id: string): Promise<void> => {
  await honeyRequest<void>({ method: 'delete', url: `/records/${encodeURIComponent(id)}` });
};

/** Max ids the bulk endpoints accept per request (server-enforced; Zod max 200). */
const BULK_MUTATION_BATCH_SIZE = 200;

/** Splits a list into consecutive chunks of at most `size` (drops nothing). */
function chunk<Item>(items: Array<Item>, size: number): Array<Array<Item>> {
  return Array.from({ length: Math.ceil(items.length / size) }, (_unused, index) =>
    items.slice(index * size, index * size + size)
  );
}

/**
 * Bulk-approve records. Best-effort: returns approved ids plus per-id skip reasons.
 * The server caps each call at {@link BULK_MUTATION_BATCH_SIZE} ids, so a larger
 * selection is approved in SEQUENTIAL batches (bounding server load) and the
 * per-batch results are merged — a whole group no longer 400s ("array too big").
 * @param {Array<string>} ids Record ids (UUIDs); any length (batched internally).
 * @returns {Promise<BulkApproveResult>}
 */
export const approveRecordsBulk = async (ids: Array<string>): Promise<BulkApproveResult> => {
  return chunk(ids, BULK_MUTATION_BATCH_SIZE).reduce<Promise<BulkApproveResult>>(
    (previous, batch) =>
      previous.then(async (merged) => {
        const result = await honeyRequest<BulkApproveResult>({
          method: 'post',
          url: '/records/approve',
          data: { ids: batch }
        });
        return {
          approved: [...merged.approved, ...(result.approved ?? [])],
          skipped: [...merged.skipped, ...(result.skipped ?? [])],
          // Dropping this here silently muted the "labels taught" toast for every
          // bulk/group approval — the backend returns it, the merge must carry it.
          aliasOutcomes: [...(merged.aliasOutcomes ?? []), ...(result.aliasOutcomes ?? [])]
        };
      }),
    Promise.resolve({ approved: [], skipped: [], aliasOutcomes: [] })
  );
};

/**
 * Bulk soft-delete records. Best-effort: returns deleted ids plus per-id skip reasons.
 * Batched the same way as {@link approveRecordsBulk} so large selections do not exceed
 * the server's per-call id cap.
 * @param {Array<string>} ids Record ids (UUIDs); any length (batched internally).
 * @returns {Promise<BulkDeleteResult>}
 */
export const deleteRecordsBulk = async (ids: Array<string>): Promise<BulkDeleteResult> => {
  return chunk(ids, BULK_MUTATION_BATCH_SIZE).reduce<Promise<BulkDeleteResult>>(
    (previous, batch) =>
      previous.then(async (merged) => {
        const result = await honeyRequest<BulkDeleteResult>({
          method: 'post',
          url: '/records/delete',
          data: { ids: batch }
        });
        return {
          deleted: [...merged.deleted, ...(result.deleted ?? [])],
          skipped: [...merged.skipped, ...(result.skipped ?? [])]
        };
      }),
    Promise.resolve({ deleted: [], skipped: [] })
  );
};

/**
 * Search the active metric catalogue by name (autocomplete source for mapping).
 * @param {string | undefined} search Case-insensitive contains-match; blank = first page.
 * @param {number | undefined} limit Maximum matches (server default 20, max 100).
 * @returns {Promise<Array<MetricLookupItem>>}
 */
export const fetchMetrics = async (
  search?: string,
  limit?: number
): Promise<Array<MetricLookupItem>> => {
  const params: Record<string, string | number> = {};
  if (search !== undefined && search.trim() !== '') params.search = search.trim();
  if (limit !== undefined) params.limit = limit;
  const response = await honeyRequest<{ items: Array<MetricLookupItem> }>({
    method: 'get',
    url: '/metrics',
    params
  });
  return response.items;
};

/**
 * Resolve specific catalogue metrics by id — the review UI uses it to label a
 * record's mapping candidates with the unit each metric expects.
 *
 * Unlike the name search this returns metrics whatever their catalogue version or
 * status: a record's candidates were stamped against the version it was mapped
 * under, which may since have been superseded, and the caller already holds the ids.
 * Unknown ids are simply absent from the result.
 * @param {Array<string>} ids The metric ids to resolve; an empty list skips the call.
 * @returns {Promise<Array<MetricLookupItem>>}
 */
export const fetchMetricsByIds = async (ids: Array<string>): Promise<Array<MetricLookupItem>> => {
  if (ids.length === 0) {
    return [];
  }
  const response = await honeyRequest<{ items: Array<MetricLookupItem> }>({
    method: 'get',
    url: '/metrics',
    params: { ids: ids.join(',') }
  });
  return response.items;
};

/**
 * List reporting periods, newest first — the source for the review UI's period
 * pickers (the edit-form period correction and the filter-bar period selector).
 * Periods are per company (OQ25): pass the record's company to get ITS calendar —
 * strictly; a company with no seeded calendar gets an empty list, never another
 * client's periods. An unscoped call returns every period.
 * @param {string | undefined} companyId Scope to this company's own calendar, or omit for all.
 * @returns {Promise<Array<PeriodLookupItem>>}
 */
export const fetchPeriods = async (companyId?: string): Promise<Array<PeriodLookupItem>> => {
  const params: Record<string, string> = {};
  if (companyId !== undefined && companyId.trim() !== '') params.companyId = companyId.trim();
  const response = await honeyRequest<{ items: Array<PeriodLookupItem> }>({
    method: 'get',
    url: '/periods',
    params
  });
  return response.items;
};

/**
 * List the selectable entities for the review UI's "re-assign entity" dropdown,
 * company → division → subdivision → site. Optionally scoped to one company so the
 * dropdown never offers another org's entities (reassignment never crosses companies).
 * @param {string | undefined} companyId Restrict to this company, or omit for all.
 * @returns {Promise<Array<EntityLookupItem>>}
 */
export const fetchEntities = async (companyId?: string): Promise<Array<EntityLookupItem>> => {
  const params: Record<string, string> = {};
  if (companyId !== undefined && companyId.trim() !== '') params.companyId = companyId.trim();
  const response = await honeyRequest<{ items: Array<EntityLookupItem> }>({
    method: 'get',
    url: '/entities',
    params
  });
  return response.items;
};

/**
 * List the seeded countries with their provinces — the source for the review UI's
 * direct country/province pickers (OQ27b: a site-less record may attach to a
 * country or province directly; a chosen site owns the location instead).
 *
 * The hierarchy is global, but the picker should not be: scoped to a company, the
 * API returns only the countries that company has sites in or already reports from,
 * instead of every seeded country in the world.
 * @param {string | undefined} companyId Restrict to this company's geography, or omit for all.
 * @returns {Promise<Array<GeographyCountry>>}
 */
export const fetchGeography = async (companyId?: string): Promise<Array<GeographyCountry>> => {
  const params: Record<string, string> = {};
  if (companyId !== undefined && companyId.trim() !== '') params.companyId = companyId.trim();
  const response = await honeyRequest<{ countries: Array<GeographyCountry> }>({
    method: 'get',
    url: '/geography',
    params
  });
  return response.countries;
};

/**
 * Fetch the original bytes of a source file as a Blob (Phase 5 traceability — the
 * permanent, auditable link back to the file a record came from, for any input
 * type). Throws a `SyntiroHoneyApiError` with status 404 when the file is
 * absent/not owned or its stored bytes are gone; callers should treat that as
 * "source unavailable".
 * @param {string} fileId Source file id (UUID).
 * @returns {Promise<Blob>} The original file bytes, typed with the server MIME type.
 */
export const fetchFileContentBlob = async (fileId: string): Promise<Blob> => {
  return honeyRequest<Blob>({
    method: 'get',
    url: `/files/${encodeURIComponent(fileId)}/content`,
    responseType: 'blob'
  });
};

/**
 * The admin file list: every file (all statuses, including soft-deleted) with
 * record counts + quality summary. Admin-gated per DECISIONS #39 (note: honey's
 * server-side role enforcement is behind its ROLE_ENFORCEMENT_ENABLED flag).
 * @param {number} limit Page size.
 * @param {number} offset Offset into the newest-first list.
 * @returns {Promise<AdminFilesResponse>}
 */
export const fetchAdminFiles = async (
  limit: number,
  offset: number
): Promise<AdminFilesResponse> => {
  return honeyRequest<AdminFilesResponse>({
    method: 'get',
    url: '/files/admin',
    params: { limit, offset }
  });
};

/**
 * Re-processes a file through the pipeline; its current records are superseded.
 * @param {string} id File id.
 * @returns {Promise<{ jobId: string }>}
 */
export const retryFile = async (id: string): Promise<{ jobId: string }> => {
  return honeyRequest<{ jobId: string }>({
    method: 'post',
    url: `/files/${encodeURIComponent(id)}/retry`
  });
};

/**
 * Restores a soft-deleted file and exactly the records its delete cascaded.
 * @param {string} id File id.
 * @returns {Promise<void>}
 */
export const restoreFile = async (id: string): Promise<void> => {
  await honeyRequest<void>({ method: 'post', url: `/files/${encodeURIComponent(id)}/restore` });
};

/**
 * A file's audit trail: attributed, timestamped lifecycle events, oldest first.
 * @param {string} id File id.
 * @returns {Promise<Array<FileEventItem>>}
 */
export const fetchFileEvents = async (id: string): Promise<Array<FileEventItem>> => {
  const response = await honeyRequest<{ items: Array<FileEventItem> }>({
    method: 'get',
    url: `/files/${encodeURIComponent(id)}/events`
  });
  return response.items;
};

/**
 * A record's audit trail: every append-only event, attributed and timestamped,
 * oldest first. The record-level complement of {@link fetchFileEvents}.
 * @param {string} id Record id.
 * @returns {Promise<Array<RecordEventItem>>}
 */
export const fetchRecordEvents = async (id: string): Promise<Array<RecordEventItem>> => {
  const response = await honeyRequest<{ items: Array<RecordEventItem> }>({
    method: 'get',
    url: `/records/${encodeURIComponent(id)}/events`
  });
  return response.items;
};

/**
 * List the caller's most recent non-deleted files, newest first (file filter source).
 * @param {number | undefined} limit Maximum files (server default 20, max 100).
 * @returns {Promise<Array<FileListItem>>}
 */
export const fetchFiles = async (limit?: number): Promise<Array<FileListItem>> => {
  const response = await honeyRequest<{ items: Array<FileListItem> }>({
    method: 'get',
    url: '/files',
    params: limit === undefined ? {} : { limit }
  });
  return response.items;
};

/**
 * List a company's learned rules (metric aliases, #43/ADR-0003), newest first.
 * @param {MetricAliasListQuery} query Company (required), status/search filters, pagination.
 * @returns {Promise<MetricAliasListResult>}
 */
export const fetchMetricAliases = async (
  query: MetricAliasListQuery
): Promise<MetricAliasListResult> => {
  const params: Record<string, string | number> = { companyId: query.companyId };
  if (query.status !== undefined) params.status = query.status;
  if (query.normalisedLabel !== undefined) params.normalisedLabel = query.normalisedLabel;
  if (query.search !== undefined) params.search = query.search;
  if (query.limit !== undefined) params.limit = query.limit;
  if (query.offset !== undefined) params.offset = query.offset;
  return honeyRequest<MetricAliasListResult>({
    method: 'get',
    url: '/metric-aliases',
    params
  });
};

/**
 * Retire an ACTIVE learned rule (reviewer undo). The rule is kept as history,
 * never deleted; 404 when it is absent, already closed, or another company's.
 * @param {string} id Alias id (UUID).
 * @param {string} companyId The rule's company (the mutation is company-guarded).
 * @param {string} reason Why the rule is being retired (kept on the row).
 * @returns {Promise<void>}
 */
export const retireMetricAlias = async (
  id: string,
  companyId: string,
  reason: string
): Promise<void> => {
  await honeyRequest<void>({
    method: 'post',
    url: `/metric-aliases/${encodeURIComponent(id)}/retire`,
    data: { companyId, reason }
  });
};

/**
 * Resolve a label to one metric from now on — the explicit human resolution that
 * re-arms a contested label (#43/ADR-0003). Retires every existing rule for the
 * (company, label) pair and creates a fresh active one.
 * @param {string} companyId The company whose label is being resolved.
 * @param {string} normalisedLabel The normalised label key, as served by the list.
 * @param {string} metricId The catalogue metric the label maps to from now on.
 * @returns {Promise<void>}
 */
export const resolveMetricAlias = async (
  companyId: string,
  normalisedLabel: string,
  metricId: string
): Promise<void> => {
  await honeyRequest<void>({
    method: 'post',
    url: '/metric-aliases/resolve',
    data: { companyId, normalisedLabel, metricId }
  });
};
